import React, { useEffect, useState } from 'react';
import RootModal from '../../modals/rootModal';
import { Button, styled, TextField, Typography } from '@mui/material';
import { changeUser, createClientLog, createUser, disableUser } from '../../../api/adminPanel';
import { colors } from '../../../styles/globalStyles';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import {
    userAtom,
    userForAdminPanelAtom,
    userKssDataForAdminPanelAtom,
    userKssTrackerDataForAdminPanelAtom,
    userValuesForAdminPanelAtom,
} from '../../../recoil/atoms/userAtom';
import { getCardsData } from '../../../api/session';
import { getFeedbackValuesData } from '../../../api/user';
import { getKssTrackerData } from '../../../util/kssTracker';
import { getTeamMembers } from '../../../api/search';
import { teamMembersForAdminPanelAtom } from '../../../recoil/atoms/teamMembersAtom';
import { getKssData } from '../../../util/kssData';
import { getUserData } from '../../../api/auth';
import { Loader } from '../../loaders/loader';
import textData from '../../../data/data.json';
import { selectedLanguageAtom } from '../../../recoil/atoms/languageAtom';

const Wrap = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 17,
    padding: 17,
    justifyContent: 'flex-start',
    height: '100%',
});

const InputLabel = styled(Typography)({
    fontSize: 30,
    lineHeight: '40.8px',
});

const Input = styled(TextField)({
    width: '100%',
});

const InputWrap = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
});

const ButtonsBar = styled('div')({
    display: 'flex',
    gap: 2,
    width: '100%',
    paddingBottom: 17,
});

const SaveButton = styled(Button)({
    width: '50%',
    padding: '14px 17.5px',
    borderRadius: 23,
});

const UserCreateModal = ({ open, onClose, teamId, selectedUser }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { userCreateModal } = textData[selectedLanguage];
    const navigate = useNavigate();

    const [isSelectedUserDataLoading, setIsSelectedUserDataLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(false);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    const user = useRecoilValue(userAtom);
    const [, setUserForAdminPanel] = useRecoilState(userForAdminPanelAtom);
    const [, setUserKssForAdminPanel] = useRecoilState(userKssTrackerDataForAdminPanelAtom);
    const [, setUserValuesForAdminPanel] = useRecoilState(userValuesForAdminPanelAtom);
    const [, setTeamMembersForAdminPanel] = useRecoilState(teamMembersForAdminPanelAtom);
    const [, setUserKssDataForAdminPanel] = useRecoilState(userKssDataForAdminPanelAtom);

    useEffect(() => {
        if (selectedUser) {
            setUsername(selectedUser.username);
            setPassword('8********');
            setFirstName(selectedUser.first_name);
            setLastName(selectedUser.last_name);
        } else {
            setUsername('');
            setPassword('');
            setFirstName('');
            setLastName('');
        }
    }, [selectedUser]);

    useEffect(() => {
        if (open !== true) {
            setIsSuccess(false);
        }
    }, [open]);

    const [errorMessage, setErrorMessage] = useState(null);
    const [errors, setErrors] = useState({
        username: true,
        password: true,
        firstName: true,
        lastName: true,
    });

    const handleSubmit = (e) => {
        setValidating(true);
        const usernameRegEx = /^[a-zA-Zа-яА-Я0-9.@+_-]{1,150}$/;
        const passwordRegEx = /^[A-Za-z0-9!@#$%^&*()_+{}\[\]:;<>,.?~\-=/]{8,20}$/;
        const nameRegEx = /^([А-Яа-яA-Za-z]+(-[А-Яа-яA-Za-z]+)*)$/;

        const newErrors = errors;

        newErrors.username = username === '' || !usernameRegEx.test(username);
        newErrors.password = !passwordRegEx.test(password);
        newErrors.firstName = firstName === '' || !nameRegEx.test(firstName);
        newErrors.lastName = lastName === '' || !nameRegEx.test(lastName);

        let errorExist = Object.values(newErrors).includes(true);

        setErrors({ ...newErrors });

        if (!errorExist) {
            if (selectedUser !== null) {
                if (password === '8********') {
                    changeUser({
                        id: selectedUser.id,
                        username: username,
                        first_name: firstName,
                        last_name: lastName,
                        team: teamId,
                    })
                        .then((res) => {
                            setErrorMessage(null);
                            if (e.target.name === 'save-and-exit') {
                                onClose();
                            } else {
                                setValidating(false);
                            }
                        })
                        .catch((e) => {
                            setErrorMessage(userCreateModal.errors[0]);
                            setErrors({ ...newErrors, username: true });
                        });
                } else {
                    changeUser({
                        id: selectedUser.id,
                        username: username,
                        password: password,
                        first_name: firstName,
                        last_name: lastName,
                        team: teamId,
                    })
                        .then((res) => {
                            setErrorMessage(null);
                            if (e.target.name === 'save-and-exit') {
                                onClose();
                            } else {
                                setValidating(false);
                            }

                            setIsSuccess(true);
                        })
                        .catch((e) => {
                            if (e.response.status === 400) {
                                setErrorMessage(userCreateModal.errors[0]);
                                setErrors({ ...newErrors, username: true });
                            } else {
                                createClientLog(e);
                                setErrorMessage(userCreateModal.errors[1]);
                            }
                            setIsSuccess(false);
                        });
                }
            } else {
                createUser({
                    username: username,
                    password: password,
                    first_name: firstName,
                    last_name: lastName,
                    team: teamId,
                })
                    .then((r) => {
                        setErrorMessage(null);
                        if (e.target.name === 'save-and-exit') {
                            onClose();
                        } else {
                            setValidating(false);
                            setErrors({
                                username: false,
                                password: false,
                                firstName: false,
                                lastName: false,
                            });
                            setUsername('');
                            setPassword('');
                            setFirstName('');
                            setLastName('');
                        }

                        setIsSuccess(true);
                    })
                    .catch((e) => {
                        if (e.response.status === 400) {
                            setErrorMessage(userCreateModal.errors[0]);
                            setErrors({ ...newErrors, username: true });
                        } else {
                            createClientLog(e);
                            setErrorMessage(userCreateModal.errors[1]);
                        }

                        setIsSuccess(false);
                    });
            }
        } else {
            setIsSuccess(false);
            setErrorMessage(userCreateModal.errors[2]);
        }
    };

    const deleteSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        disableUser(selectedUser.id)
            .then((res) => {
                setLoading(false);
                onClose();
            })
            .catch((e) => {
                createClientLog(e);
                setErrorMessage(userCreateModal.errors[1]);
            });
    };

    function handleUserPanelButtonClick() {
        localStorage.setItem('user-id-for-admin-panel', selectedUser.id);

        setIsSelectedUserDataLoading(true);

        const userPromise = getUserData(selectedUser.id)
            .then((user) => {
                setUserForAdminPanel(user);
            })
            .catch((error) => {
                console.error('Ошибка получения данных пользователя', error);
            });

        const cardsPromise = getCardsData(selectedUser.id, selectedUser.teams?.[0].id)
            .then(({ data: kss }) => {
                if (!kss.length) {
                    setUserKssForAdminPanel([]);
                    return;
                }

                const getItemByStatus = (status) => kss.filter((i) => i.status === status)[0];
                const kssData = [getItemByStatus('keep'), getItemByStatus('stop'), getItemByStatus('start')];

                setUserKssForAdminPanel(kssData);
            })
            .catch((error) => {
                console.error('Ошибка получения КСС', error);
            });

        const valuesPromise = getFeedbackValuesData(selectedUser.id, selectedUser.id)
            .then(({ data: { values } }) => {
                setUserValuesForAdminPanel(values);
            })
            .catch((error) => {
                console.error('Ошибка получения ценностей', error);
            });

        // const trackerPromise = getKssTrackerData(selectedUser.id)
        //     .then((kssTrackerData) => {
        //         setUserTrackerForAdminPanel(kssTrackerData);
        //     })
        //     .catch((error) => {
        //         console.error('Ошибка получения данных КСС трекера', error);
        //     });

        const teamPromise = getTeamMembers(selectedUser.id)
            .then((teamMembers) => {
                const teamMembersWithNames = teamMembers.data.map((teamMember) => ({
                    ...teamMember,
                    name: teamMember.first_name + " " + (teamMember.last_name ? teamMember.last_name[0] + "." : ""),
                }));

                setTeamMembersForAdminPanel(teamMembersWithNames);
            })
            .catch((error) => {
                console.error('Ошибка получения участников команды', error);
            });

        const kssDataPromise = getKssData(selectedUser.id)
            .then((kssData) => {
                setUserKssDataForAdminPanel(kssData);
            })
            .catch((error) => {
                console.error('Ошибка получения КСС', error);
            });

        Promise.all([userPromise, cardsPromise, valuesPromise, teamPromise, kssDataPromise])
            .then(() => {
                setIsSelectedUserDataLoading(false);
                navigate('/');
            })
            .catch((error) => {
                setIsSelectedUserDataLoading(false);
                console.error('Ошибка при загрузке данных для админки', error);
            });
    }

    return (
        <RootModal open={open} onClose={onClose}>
            <Wrap>
                <InputWrap>
                    <InputLabel variant={'h1'}>{userCreateModal.username.title}</InputLabel>
                    <Input
                        placeholder={userCreateModal.username.placeholder}
                        value={username}
                        helperText={errors.username && validating ? userCreateModal.username.error : ''}
                        error={errors.username && validating}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                </InputWrap>
                <InputWrap>
                    <InputLabel variant={'h1'}>{userCreateModal.password.title}</InputLabel>
                    <Input
                        placeholder={userCreateModal.password.placeholder}
                        value={password}
                        type={password === '8********' ? 'password' : null}
                        helperText={errors.password && validating ? userCreateModal.password.error : ''}
                        error={errors.password && validating}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={(e) => (password === '8********' ? setPassword('') : null)}
                    />
                </InputWrap>
                <InputWrap>
                    <InputLabel variant={'h1'}>{userCreateModal.first_name.title}</InputLabel>
                    <Input
                        placeholder={userCreateModal.first_name.placeholder}
                        value={firstName}
                        helperText={errors.firstName && validating ? userCreateModal.first_name.error : ''}
                        error={errors.firstName && validating}
                        onChange={(e) => setFirstName(e.target.value)}
                    />
                </InputWrap>
                <InputWrap>
                    <InputLabel variant={'h1'}>{userCreateModal.last_name.title}</InputLabel>
                    <Input
                        placeholder={userCreateModal.last_name.placeholder}
                        value={lastName}
                        helperText={errors.lastName && validating ? userCreateModal.last_name.error : ''}
                        error={errors.lastName && validating}
                        onChange={(e) => setLastName(e.target.value)}
                    />
                </InputWrap>
                <Typography style={{ color: colors.red }}>{errorMessage}</Typography>
                {isSuccess ? (
                    <Typography style={{ color: colors.green }}>
                        {userCreateModal.success.title}{' '}
                        {selectedUser === null
                            ? userCreateModal.success.addedTitle
                            : userCreateModal.success.editedTitle}
                    </Typography>
                ) : null}
                <ButtonsBar>
                    {selectedUser !== null ? (
                        <SaveButton
                            disabled={loading}
                            name={'delete-and-exit'}
                            variant={'coloredLight'}
                            onClick={(e) => deleteSubmit(e)}
                            style={{ width: '100%', background: 'rgb(245, 66, 111)' }}
                        >
                            {userCreateModal.buttons[0]}
                        </SaveButton>
                    ) : null}

                    <SaveButton
                        disabled={loading}
                        name={'save-and-exit'}
                        variant={'coloredLight'}
                        onClick={(e) => handleSubmit(e)}
                        style={selectedUser !== null ? { width: '100%', height: 72 } : null}
                    >
                        {userCreateModal.buttons[1]}
                    </SaveButton>
                    {selectedUser === null ? (
                        <SaveButton
                            disabled={loading}
                            name={'save-and-add-more'}
                            variant={'colored'}
                            onClick={(e) => handleSubmit(e)}
                        >
                            {userCreateModal.buttons[2]}
                        </SaveButton>
                    ) : null}
                </ButtonsBar>
                <ButtonsBar>
                    {user?.isLeader && selectedUser && (
                        <SaveButton
                            disabled={loading}
                            name={'selectedUser-panel'}
                            variant={'colored'}
                            onClick={handleUserPanelButtonClick}
                            sx={{
                                width: '100%',
                                height: 72,
                            }}
                        >
                            {isSelectedUserDataLoading ? <Loader /> : userCreateModal.buttons[3]}
                        </SaveButton>
                    )}
                </ButtonsBar>
            </Wrap>
        </RootModal>
    );
};

export default UserCreateModal;
